import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import React, { useState } from "react";
import ModalWrapper, { modalSubmitButtonSx } from "../../shared/ModalWrapper";

interface IProps {
  open: boolean;
  adminName: string;
  password: string;
  onClose: () => void;
}

/**
 * Shows a new admin's temporary password. No email goes out yet, and the API
 * never returns the password again, so this stays open until dismissed rather
 * than living in a toast that closes on its own.
 */
const TempPasswordDialog: React.FC<IProps> = ({ open, adminName, password, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      title="Admin created"
      maxWidth="xs"
      actions={
        <Button variant="contained" onClick={onClose} sx={modalSubmitButtonSx}>
          Done
        </Button>
      }
    >
      <Typography variant="body1" sx={{ mb: 2 }}>
        Admin <strong>{adminName}</strong> was created. Share this temporary password with them:
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: "8px",
          backgroundColor: "#F5F5F5",
          fontFamily: "monospace",
          fontSize: "15px",
          wordBreak: "break-all",
        }}
      >
        <span data-testid="temp-password">{password}</span>
        <Tooltip title={copied ? "Copied" : "Copy"}>
          <IconButton size="small" onClick={handleCopy} aria-label="Copy password">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" sx={{ color: "#878787", mt: 2 }}>
        This password won't be shown again.
      </Typography>
    </ModalWrapper>
  );
};

export default TempPasswordDialog;
